// Home route — SSR head() with locale-aware SEO tags derived from the
// request Host header. Body continues to render via the App bridge until
// the rest of the SPA migration lands.
import { createFileRoute } from "@tanstack/react-router";
import { getHost } from "@/lib/host";
import Index from "@/pages/Index";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";

const SEO: Record<LocaleKey, { title: string; description: string; lang: string }> = {
  es: {
    title: "Fora da Rota | Viajes Únicos a Brasil",
    description:
      "Descubre Brasil con Fora da Rota. Hoteles, resorts y experiencias auténticas en los mejores destinos brasileños.",
    lang: "es",
  },
  "pt-BR": {
    title: "Fora da Rota | Viagens Únicas pelo Brasil",
    description:
      "Descubra o Brasil com Fora da Rota. Hotéis, resorts e experiências autênticas nos melhores destinos.",
    lang: "pt-BR",
  },
  en: {
    title: "Fora da Rota | Unique Travel to Brazil",
    description:
      "Discover Brazil with Fora da Rota. Hotels, resorts and authentic experiences in the best Brazilian destinations.",
    lang: "en",
  },
  "en-EU": {
    title: "Fora da Rota | Unique Travel to Brazil",
    description:
      "Discover Brazil with Fora da Rota. Hotels, resorts and authentic experiences in the best Brazilian destinations.",
    lang: "en",
  },
};

export const Route = createFileRoute("/")({
  loader: () => {
    const host = getHost();
    const locale = localeFromHost(host);
    const origin = canonicalOriginFromHost(host);
    return { locale, origin };
  },
  head: ({ loaderData }) => {
    const locale = loaderData?.locale ?? "es";
    const origin = loaderData?.origin ?? "https://www.foradarota.com";
    const seo = SEO[locale];
    const url = `${origin}/`;

    // Hero image URLs — sincronizar com src/components/Hero.tsx.
    // Qualidade reduzida no mid-range para cortar peso do LCP em mobile
    // (DPR>2 pega candidato ~1080–1280w, que dominava 296 KB no Lighthouse).
    const heroBase =
      "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?auto=format&fit=crop";
    const heroImg480 = `${heroBase}&w=480&q=35`;
    const heroImg768 = `${heroBase}&w=768&q=38`;
    const heroImg1080 = `${heroBase}&w=1080&q=38`;
    const heroImg1280 = `${heroBase}&w=1280&q=40`;
    const heroImg1920 = `${heroBase}&w=1920&q=50`;
    const heroSrcset = `${heroImg480} 480w, ${heroImg768} 768w, ${heroImg1080} 1080w, ${heroImg1280} 1280w, ${heroImg1920} 1920w`;

    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: `${origin}/og-image.jpg` },
        { name: "twitter:title", content: seo.title },
        { name: "twitter:description", content: seo.description },
        { name: "twitter:image", content: `${origin}/og-image.jpg` },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflangLinks("/"),
        // Fase 4 · P4-A #1 — Preload responsivo do hero (LCP mobile).
        // srcset/sizes IDÊNTICOS ao <img> em Hero.tsx para que o browser
        // baixe uma única variante correta ao invés de duas.
        {
          rel: "preload",
          as: "image",
          href: heroImg480,
          imagesrcset: heroSrcset,
          imagesizes: "100vw",
          fetchpriority: "high",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            name: "Fora da Rota",
            url: origin,
            description: seo.description,
            inLanguage: seo.lang,
            areaServed: "BR",
          }),
        },
      ],
    };
  },
  component: Index,
});
